import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../../config';
import { useTheme } from '../../context/ThemeContext';


const Appearance: React.FC = () => {
  const { theme, setTheme } = useTheme();
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleThemeChange = async (newTheme: 'light' | 'dark') => {
    setSaving(true);
    setMessage(null);

    try {
      // Update theme in backend
      await axios.put(`${API_URL}/api/auth/me`, {
        theme: newTheme
      });

      // Update local theme
      setTheme(newTheme);

      // Update user in localStorage
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        user.theme = newTheme;
        localStorage.setItem('user', JSON.stringify(user));
      }

      setMessage({ type: 'success', text: 'Theme preference saved successfully' });
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to save theme preference' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-600 rounded-lg border border-gray-200 dark:border-gray-500 p-6">
      <h2 className="text-xl font-sans font-bold normal-case text-gray-800 dark:text-white mb-6">Appearance</h2>

      {message && (
        <div className={`mb-4 px-4 py-3 rounded border font-sans ${
          message.type === 'success'
            ? 'bg-green-300 border-green-400 text-gray-800'
            : 'bg-red-300 border-red-400 text-gray-800'
        }`}>
          {message.text}
        </div>
      )}

      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white font-sans normal-case mb-4">Theme</h3>
          <p className="text-sm text-gray-600 dark:text-gray-200 font-sans mb-4">
            Choose your preferred color scheme for the application.
          </p>

          <div className="grid grid-cols-2 gap-4">
            {/* Light Theme Option */}
            <button
              onClick={() => handleThemeChange('light')}
              disabled={saving}
              className={`relative p-6 rounded-lg border-2 transition ${
                theme === 'light'
                  ? 'border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-900/30'
                  : 'border-gray-300 dark:border-gray-500 hover:border-gray-400 dark:hover:border-gray-400'
              } disabled:opacity-50`}
            >
              <div className="flex flex-col items-center gap-3">
                <div className="w-16 h-16 rounded-lg bg-white border-2 border-gray-300 shadow-md flex items-center justify-center">
                  <span className="text-3xl">☀️</span>
                </div>
                <div className="text-center">
                  <p className="font-semibold text-gray-800 dark:text-white font-sans">Light</p>
                  <p className="text-xs text-gray-600 dark:text-gray-300 font-sans">Default bright theme</p>
                </div>
              </div>
              {theme === 'light' && (
                <div className="absolute top-2 right-2">
                  <span className="text-blue-600 dark:text-blue-400 text-xl">✓</span>
                </div>
              )}
            </button>

            {/* Dark Theme Option */}
            <button
              onClick={() => handleThemeChange('dark')}
              disabled={saving}
              className={`relative p-6 rounded-lg border-2 transition ${
                theme === 'dark'
                  ? 'border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-900/30'
                  : 'border-gray-300 dark:border-gray-500 hover:border-gray-400 dark:hover:border-gray-400'
              } disabled:opacity-50`}
            >
              <div className="flex flex-col items-center gap-3">
                <div className="w-16 h-16 rounded-lg bg-gray-900 border-2 border-gray-700 shadow-md flex items-center justify-center">
                  <span className="text-3xl">🌙</span>
                </div>
                <div className="text-center">
                  <p className="font-semibold text-gray-800 dark:text-white font-sans">Dark</p>
                  <p className="text-xs text-gray-600 dark:text-gray-300 font-sans">Easy on the eyes</p>
                </div>
              </div>
              {theme === 'dark' && (
                <div className="absolute top-2 right-2">
                  <span className="text-blue-600 dark:text-blue-400 text-xl">✓</span>
                </div>
              )}
            </button>
          </div>
        </div>

        {saving && (
          <div className="text-center text-sm text-gray-600 dark:text-gray-200 font-sans">
            Saving your preference...
          </div>
        )}
      </div>
    </div>
  );
};

export default Appearance;
